import { queryOptions } from "@tanstack/react-query";
import { getAccessToken } from "@/auth/tokens";
import { API_BASE_URL, api } from "./client";
import { payload } from "./envelope";
import { ApiError, errorCode, errorMessage } from "./errors";
import { unwrapNoContent } from "./queries";
import type { components } from "./schema";

/**
 * O Bearer para as chamadas que NÃO passam pelo cliente tipado (hoje só o
 * upload multipart da foto de perfil). Sem sessão o header sai vazio e o
 * servidor responde 401 — que é o comportamento certo, não um erro a
 * tratar aqui.
 */
function authorizationHeader(): HeadersInit {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type EmbeddedSignupConfig = components["schemas"]["EmbeddedSignupConfig"];
export type WabaPhone = components["schemas"]["WabaPhone"];
export type WhatsappReadiness = components["schemas"]["WhatsappReadiness"];

/**
 * Abre o Embedded Signup: devolve o `app_id`/`config_id` que o SDK da Meta
 * precisa. A config é **estática** — não é dado por-estabelecimento, apesar
 * do `establishment_id` no corpo.
 *
 * Duas coisas fazem disto uma chamada de carregamento de tela, não de
 * clique:
 *
 * 1. O `FB.init` precisa do `app_id` **antes** do `FB.login`. Esperar a
 *    rede dentro do clique faz o popup deixar de ser consequência direta do
 *    gesto do usuário, e o browser bloqueia.
 * 2. O `409` (já existe número ativo) é justamente o que decide se a tela
 *    mostra "conectar" ou "conectado" — informação que precisa chegar antes
 *    de desenhar, não depois de clicar.
 *
 * Erros possíveis: `409 waba_phone_already_active` e `422
 * meta_not_configured` (deploy sem `META_APP_ID`/`META_CONFIG_ID` — falha
 * nossa, não do dono).
 */
export function startEmbeddedSignup(
  establishmentId: string,
): Promise<EmbeddedSignupConfig> {
  return payload(
    api.POST("/waba/onboarding/embedded-signup", {
      body: { establishment_id: establishmentId },
    }),
  );
}

/**
 * Troca o `authorization_code` do SDK pelo token permanente e grava o
 * número como `pending_verification`.
 *
 * **Um erro aqui não significa que o número não foi registrado.** A
 * inscrição nos webhooks roda depois da gravação de propósito: o
 * `authorization_code` é de uso único, então abortar antes de gravar
 * obrigaria o dono a refazer todo o Embedded Signup. Quem chama trata falha
 * como "registrado, precisa retentar a ativação" — nunca como "recomece do
 * zero".
 *
 * O código expira em ~30 segundos do lado da Meta, então isto tem que ser
 * chamado imediatamente depois do callback do SDK, sem passo intermediário.
 */
export function registerWabaPhone(
  establishmentId: string,
  authorizationCode: string,
): Promise<WabaPhone> {
  return payload(
    api.POST("/waba/phone", {
      body: {
        establishment_id: establishmentId,
        authorization_code: authorizationCode,
      },
    }),
  );
}

/**
 * Fecha a ativação: reafirma a inscrição nos webhooks, registra o número na
 * Cloud API e só então muda o status para `active`.
 *
 * Idempotente por contrato — o invariante da API é que `active` SEMPRE
 * implica inscrito. Por isso este é também o ponto de retentativa quando o
 * `registerWabaPhone` falhou depois de gravar: chamar de novo é seguro.
 */
export function verifyWabaPhone(id: string): Promise<WabaPhone> {
  return payload(api.POST("/waba/phone/{id}/verify", { params: { path: { id } } }));
}

/**
 * "Já está funcionando?" — e a resposta honesta é **em parte**.
 *
 * O bot **atende** assim que o número conecta: responder quem escreve é
 * mensagem de sessão, dentro da janela de 24h da Meta, e não depende de
 * aprovação nenhuma. O que depende de template aprovado é **iniciar**
 * conversa — lembrete e aviso de vaga.
 *
 * Esta é também a única fonte de verdade persistente sobre "tem WhatsApp
 * conectado?": `finishOnboarding()` mora em `sessionStorage` e morre com a
 * aba, então quem pulou a conexão no onboarding só é reencontrado por aqui.
 */
export function whatsappReadinessQuery() {
  return queryOptions({
    queryKey: ["waba", "readiness"] as const,
    queryFn: () => payload(api.GET("/waba/readiness")),
  });
}

/**
 * O número corrente do estabelecimento — **incluindo** o que ainda está em
 * `pending_verification`, que o readiness não enxerga (lá só conta número
 * ativo).
 *
 * É o que torna uma ativação interrompida recuperável. O `id` só aparece
 * na resposta do registro, então sem esta consulta quem fechasse a aba no
 * meio não teria como chamar o `/verify` e precisaria refazer o Embedded
 * Signup inteiro.
 *
 * Devolve `null` quando ainda não há número: é resposta legítima, não erro.
 */
export function wabaPhoneQuery() {
  return queryOptions({
    queryKey: ["waba", "phone"] as const,
    queryFn: () => payload(api.GET("/waba/phone")),
  });
}

/**
 * Desconecta o número, liberando o estabelecimento pra conectar outro.
 *
 * O registro não é apagado — reconectar o mesmo número depois reaproveita
 * a linha, preservando o histórico de conversa que aponta pra ela.
 */
export function disconnectWabaPhone(id: string): Promise<void> {
  return unwrapNoContent(api.DELETE("/waba/phone/{id}", { params: { path: { id } } }));
}

/**
 * O `startEmbeddedSignup` modelado como carregamento de tela.
 *
 * É `POST` no contrato, mas não tem efeito colateral — devolve config
 * estática. Tratar como query é o que permite pedir a config **antes** do
 * clique, que é o que evita o bloqueio de popup.
 *
 * `retry: false` porque os dois erros que interessam (`409` já conectado,
 * `422` servidor sem credenciais da Meta) são respostas definitivas, não
 * falhas transitórias: repetir só atrasa a tela.
 *
 * **A chave fica FORA da árvore `["waba"]`, e isso não é organização.**
 *
 * Todo o resto da tela invalida `["waba"]` o tempo todo: depois de
 * registrar o número, depois de ativar, e a cada evento `whatsapp` do
 * tempo real (a Meta aprovando um template dispara um). Com a chave
 * dentro dessa árvore, cada uma dessas invalidações refazia este POST —
 * e o que voltava, assim que o número ficava ativo, era `409
 * waba_phone_already_active`. Um erro registrado no servidor a cada
 * conexão bem-sucedida, por uma requisição que ninguém queria fazer.
 *
 * Como a config é estática (`staleTime` infinito), ela não tem por que
 * ser revalidada por nada disso. O que decide se a tela mostra
 * "conectar" ou "conectado" é `wabaPhoneQuery`, não esta.
 */
export function embeddedSignupConfigQuery(establishmentId: string) {
  return queryOptions({
    queryKey: ["waba-signup-config", establishmentId] as const,
    queryFn: () => startEmbeddedSignup(establishmentId),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    // Nem mesmo ao voltar pra aba: config de servidor não muda enquanto
    // a pessoa estava no e-mail.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export type WhatsappDiagnostics = components["schemas"]["WhatsappDiagnostics"];
export type TemplateStatusEvent = components["schemas"]["TemplateStatusEvent"];

/**
 * Concilia a revisão dos templates direto na Meta.
 *
 * O status local só se move pelo webhook `message_template_status_update`,
 * e ele avisa quando a revisão **muda**. Aprovação ocorrida antes de
 * existir registro nosso — ou com o webhook fora do ar naquele instante —
 * não volta sozinha: o template fica pendente aqui e aprovado lá, e a tela
 * manda o dono esperar por algo já resolvido.
 *
 * Idempotente, e é o que dá sentido a um botão "verificar agora": sem
 * isso, a única saída seria esperar um evento que talvez nunca venha.
 */
export function syncTemplates(): Promise<TemplateStatusEvent[]> {
  return payload(api.POST("/templates/sync"));
}

/**
 * Pergunta à Meta o que o dono quer saber quando desconfia do WhatsApp:
 * qual número está conectado, como a Meta avalia esse número, e se o que
 * os clientes mandam chega até nós.
 *
 * Vale o diagnóstico separado porque é a falha que não tem sintoma: a
 * inscrição pode cair (token revogado, provedor trocado) sem que nada aqui
 * mude — o número segue ativo, o envio segue funcionando, e só o
 * recebimento morre. Do nosso lado tudo parece certo.
 *
 * Bate na Meta a cada chamada, então é sob demanda, nunca em laço.
 */
export function whatsappDiagnostics(): Promise<WhatsappDiagnostics> {
  return payload(api.GET("/waba/diagnostics"));
}

export type BusinessProfileView = components["schemas"]["BusinessProfileView"];
export type BusinessProfile = components["schemas"]["BusinessProfile"];
export type BusinessProfileUpdate = components["schemas"]["BusinessProfileUpdate"];
export type BusinessVertical = components["schemas"]["BusinessVertical"];
export type ExternalProfileTask = components["schemas"]["ExternalProfileTask"];

/**
 * O perfil comercial que o cliente vê ao tocar no nome do negócio dentro
 * da conversa.
 *
 * Lê ao vivo na Meta a cada chamada — não há cópia no nosso banco, porque
 * o dono também pode editar pelo WhatsApp Manager e uma cópia começaria
 * certa e terminaria mostrando o endereço antigo. Por isso `staleTime` é
 * curto e a query não fica em laço: é leitura de abertura de tela.
 *
 * A resposta traz junto as categorias aceitas e os limites de cada campo.
 * Eles vêm do servidor de propósito: duplicados aqui, sairiam de sincronia
 * na primeira vez que a Meta mudasse um limite, e a tela passaria a
 * recusar (ou deixar passar) o que o servidor não recusa.
 */
export function businessProfileQuery() {
  return queryOptions({
    queryKey: ["waba", "profile"] as const,
    queryFn: () => payload(api.GET("/waba/profile")),
    // Sem número conectado o servidor responde 422 — resposta definitiva,
    // não falha transitória. Repetir só atrasa a tela.
    retry: false,
  });
}

/**
 * Grava o perfil. **Só mande o que o dono editou.**
 *
 * A atualização é parcial no servidor e na Meta: campo ausente fica como
 * está, campo presente com string vazia APAGA. Mandar o formulário
 * inteiro a cada salvamento funcionaria, mas apagaria na Meta qualquer
 * campo que a tela não tivesse carregado.
 */
export function updateBusinessProfile(
  update: BusinessProfileUpdate,
): Promise<BusinessProfileView> {
  return payload(api.PUT("/waba/profile", { body: update }));
}

/**
 * Troca a foto do perfil.
 *
 * Sai do `openapi-fetch` e vai de `fetch` cru porque é multipart: o
 * cliente tipado serializa corpo como JSON, e o `Content-Type` de um
 * multipart precisa carregar o `boundary` que só o browser sabe gerar —
 * defini-lo à mão é o erro que produz um 400 sem explicação.
 *
 * O Bearer é lido na hora, e não injetado pelo middleware do cliente
 * tipado, pelo mesmo motivo: esta chamada não passa por ele.
 */
export async function updateBusinessProfilePicture(
  file: File,
): Promise<BusinessProfileView> {
  const form = new FormData();
  form.append("file", file);

  const response = await fetch(`${API_BASE_URL}/v1/waba/profile/picture`, {
    method: "PUT",
    headers: authorizationHeader(),
    body: form,
  });

  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(
      errorCode(json) ?? "unknown_error",
      errorMessage(json, "Não foi possível enviar a imagem."),
    );
  }
  if (!json || typeof json !== "object" || !("data" in json)) {
    throw new ApiError("malformed_response", "A resposta veio sem `data`.");
  }
  return (json as { data: BusinessProfileView }).data;
}

/**
 * Pede à Meta a troca do nome que o cliente vê na conversa.
 *
 * **Não confundir com o resto do perfil: aqui nada muda na hora.** A Meta
 * analisa, e o desfecho chega depois pelo webhook — o servidor responde
 * 202, e a tela precisa dizer "em análise", nunca "salvo". Até a
 * aprovação, `display_name` continua sendo o nome antigo, que é o que o
 * cliente de fato vê.
 *
 * Limite da Meta: 10 trocas por período de 30 dias.
 */
export async function requestDisplayName(name: string): Promise<void> {
  await payload(api.POST("/waba/profile/display-name", { body: { name } }));
}
